<template>
  <div>
    <SModal
      v-bind="$attrs"
      v-model="displayModal"
      data-cy="calendar-event-creation-modal"
      :show-footer-gradient="component !== 'event-creation-modal-details'"
      :refocus-first-element="component"
      :header-title="headerTitle"
      :header-show-default-left-action="headerLeftAction"
      @hide="close"
      @header-left-action="$router.go(-1)"
    >
      <!-- main-pages -->
      <component
        :is="component"
        v-if="displayModal && !$wait.waiting('re-fetch-event')"
        :event="localEvent"
        :locations="locations"
        :requesting-api="$wait.waiting('create-update-event')"
        :is-edit-mode="isEditMode"
        :is-submit-disabled="!pendingChanges"
        @update-locations="(el) => (locations = el)"
        @update="updateEvent"
        @submit="submit"
      />
    </SModal>
    <SConfirmModal
      v-model="isConfirmLosingChangesModalDisplayed"
      :title="$t('calendar.event_creation.close_modal.title')"
      :description="$t('calendar.event_creation.close_modal.description')"
      :ok-title="$t('calendar.event_creation.close_modal.submit')"
      :cancel-title="$t('calendar.event_creation.close_modal.cancel')"
      :prevent-focus-return-on-modal-close="hasConfirmedLosingChanges"
      @ok="
        () => {
          hasConfirmedLosingChanges = true;
          resetAndEmit();
        }
      "
      @hide="isConfirmLosingChangesModalDisplayed = false"
    />
  </div>
</template>

<script>
import EventCreationModalDetails from 'src/components/calendar/event/creation/EventCreationModalDetails.vue'
import EventCreationModalCategories from 'src/components/calendar/event/creation/EventCreationModalCategories.vue'
import EventCreationModalLocations from 'src/components/calendar/event/creation/EventCreationModalLocations.vue'
import EventCreationModalParticipants from 'src/components/calendar/event/creation/EventCreationModalParticipants.vue'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import calendarMixin from 'src/mixins/channels/calendar/calendarMixin.js'
import { CalendarEvent } from 'src/store/models/channel/index.js'

import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'

import dateTimePicker from 'src/mixins/miscellaneous/dateTimePicker.js'
import { categories } from 'src/views/Channels/Calendar/calendarCategories.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'EventCreationModalWrapper',

  components: {
    EventCreationModalLocations,
    EventCreationModalCategories,
    EventCreationModalDetails,
    EventCreationModalParticipants,
  },

  mixins: [isMobileDeviceMixing, dateTimePicker, calendarMixin],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    displayModal: false,
    isConfirmLosingChangesModalDisplayed: false,
    localEvent: null,
    originalEvent: null,
    participants: [],
    locations: [],
    pendingChanges: false,
    hasConfirmedLosingChanges: false,
  }),

  computed: {
    component() {
      if (this.$route.query['event-locations']) return 'event-creation-modal-locations'
      if (this.$route.query['event-categories']) return 'event-creation-modal-categories'
      if (this.$route.query['event-participants']) return 'event-creation-modal-participants'
      return 'event-creation-modal-details'
    },

    headerLeftAction() {
      return this.component !== 'event-creation-modal-details'
    },

    headerTitle() {
      if (this.component === 'event-creation-modal-categories') return this.$t('calendar.event_creation.categories.title')
      if (this.component === 'event-creation-modal-participants') return this.$t('calendar.event_creation.participants.title')
      if (this.component === 'event-creation-modal-locations') return this.$t('calendar.event_creation.locations.title')
      if (this.component === 'event-creation-modal-details' && this.isEditMode) return this.$t('calendar.event_creation.title.edit')
      return this.$t('calendar.event_creation.title.create')
    },

    isEditMode() {
      return !!this.$route.query.id
    },

    eventFromStore() {
      return this.$store.getters['calendar/GET_EVENT']
    },

    isListView() {
      return this.$route.name === 'calendar-list'
    },
  },

  emits: ['close', 'create-update-event'],

  methods: {
    async close(event) {
      if (!this.displayModal) return
      if (!this.pendingChanges) return await this.resetAndEmit()

      // prevent closing modal before displaying confirmation modal in case of editing event
      event?.preventDefault()

      this.isConfirmLosingChangesModalDisplayed = true
    },

    submit: waitFor('create-update-event', async function () {
      // The event is allday if it was explicity set or if the event spans the whole day
      const isAllDay =
        this.localEvent.is_all_day ||
        (this.getDateTime(this.localEvent.begins_at).time === '00:00:00' && this.getDateTime(this.localEvent.ends_at).time === '23:59:59')

      const beginsAt = isAllDay
        ? this.$dateFormat.timestamp(this.$moment(this.localEvent.begins_at).startOf('day'))
        : this.localEvent.begins_at
      const endsAt = isAllDay ? this.$dateFormat.timestamp(this.$moment(this.localEvent.ends_at).endOf('day')) : this.localEvent.ends_at

      const payload = {
        ...this.localEvent,
        // the API expects only IDs for channels and users
        channels: this.localEvent.channels?.map((el) => el.id) || [],
        users: this.localEvent.users?.map((el) => el.id) || [],
        begins_at: beginsAt,
        ends_at: endsAt,
        // The event is allday if it was explicity set or if the event spans the whole day
        is_all_day: isAllDay,
      }
      if (this.isEditMode) {
        await CalendarEvent.$update(payload)
      } else {
        await CalendarEvent.$create(payload)
      }
      await this.$nextTick()
      await this.resetAndEmit()
      this.$emit('create-update-event')
    }),

    async resetAndEmit() {
      this.isListView && (await this.$router.replace({ query: { id: undefined } }))
      this.$store.commit('calendar/SET_EVENT', undefined)
      this.$emit('close')
    },

    updateEvent(updatedEvent) {
      if (!updatedEvent || !Object.keys(updatedEvent).length) return

      // Tech debt: until backend also returns the complete invites users (currently only channels), we need to make sure
      // that we only compare channelId and userIds for participants
      const originalEventCleanedUp = {
        ...this.originalEvent,
        channels: this.getParticipantIds(this.originalEvent.channels),
        users: this.getParticipantIds(this.originalEvent.users),
        is_notifiable: undefined, // set is_notifiable to 'undefined' to ignore it in the comparison
      }
      const updatedEventCleanedUp = {
        ...updatedEvent,
        channels: this.getParticipantIds(updatedEvent.channels),
        users: this.getParticipantIds(updatedEvent.users),
        is_notifiable: undefined, // set is_notifiable to 'undefined' to ignore it in the comparison
      }
      this.pendingChanges = !this.$compare(originalEventCleanedUp, updatedEventCleanedUp)

      this.localEvent = this.$clone(updatedEvent)
    },

    getParticipantIds(participants) {
      return participants?.map((participant) => participant.id)
    },

    reFetchEvent: waitFor('re-fetch-event', async function () {
      await this.fetchEventById(this.$route.query.id)
      this.localEvent = this.transformUserChannelParticipants(this.$clone(this.localEvent))
      this.originalEvent = this.$clone(this.localEvent)
    }),

    async onModalVisible() {
      // edit existing event
      if (this.isEditMode) {
        // if this.eventFromStore is now empty, user refreshed the page while editing -> re-fetch the event
        if (!this.eventFromStore || !Object.keys(this.eventFromStore).length) {
          return await this.reFetchEvent()
        }

        this.localEvent = this.transformUserChannelParticipants(this.eventFromStore)

        // to keep track of any changes made, save the original event
        this.originalEvent = this.$clone(this.localEvent)
        return
      }

      // create new event -> pre-fill with data
      this.localEvent = new CalendarEvent({
        category: categories.MEETING.id,
        begins_at: this.$dateFormat.timestamp(this.$moment().startOf('day')),
        ends_at: this.$dateFormat.timestamp(this.$moment().endOf('day')),
      })

      // to keep track of any changes made, save the original event
      this.originalEvent = this.$clone(this.localEvent)
    },
  },

  watch: {
    modelValue: {
      handler: function (newVal) {
        this.displayModal = newVal
        if (!this.displayModal) return
        this.onModalVisible()
      },

      immediate: true,
    },
  },
}
</script>
